import React, { useState } from 'react'
import { Button, Toaster } from '@ui'
import { Menu, MenuItem, Position } from '@blueprintjs/core'
import { Popover } from '@ui'
import api from '@api'
import { ISelection } from '@hooks/useSelection'
import track from '@track'
import Event from '@track/Event'
import { pluralize } from '@utils'
import { ApolloError } from 'apollo-boost'

interface RemoteNoDeskNeededToggleProps {
  disabled: boolean
  selection: ISelection
  context: 'remote' | 'noDeskNeeded'
  reload: () => void
}

export default function RemoteNoDeskNeededToggle({
  disabled,
  selection,
  context,
  reload,
}: RemoteNoDeskNeededToggleProps) {
  const [loading, setLoading] = useState(false)

  return (
    <Popover
      disabled={disabled}
      minimal
      position={Position.BOTTOM}
      modifiers={{ arrow: { enabled: false } }}
      content={
        <Menu>
          <MenuItem
            text={`Mark as ${context === 'remote' ? 'Remote' : 'No Desk Needed'}`}
            data-button={context === 'remote' ? 'mark_remote' : 'mark_no_desk_needed'}
            onClick={() => {
              setLoading(true)
              api.employee
                .toggleRemote({
                  ids: Object.keys(selection.items),
                  ...(context === 'remote' ? { isRemote: true } : { noDeskNeeded: true }),
                })
                .then(() => setLoading(false))
                .then(() => reload())
                .then(() =>
                  Toaster.success({
                    message: `${selection.count} Employee${pluralize(selection.count)} Mark as ${
                      context === 'remote' ? 'Remote' : 'No Desk Needed'
                    }`,
                  })
                )
                .catch((error: ApolloError) =>
                  Toaster.fail({
                    error,
                  })
                )

              track.employees({
                eventName: Event.BATCH_ACTION,
                props: {
                  actionName: 'Remote status',
                  actionValue: 'Mark as "Remote',
                  batchCount: selection.count,
                },
              })
            }}
          />
          <MenuItem
            text={`Unmark as ${context === 'remote' ? 'Remote' : 'No Desk Needed'}`}
            data-button={context === 'remote' ? 'unmark_remote' : 'unmark_no_desk_needed'}
            onClick={() => {
              setLoading(true)
              api.employee
                .toggleRemote({
                  ids: Object.keys(selection.items),
                  ...(context === 'remote' ? { isRemote: false } : { noDeskNeeded: false }),
                })
                .then(() => setLoading(false))
                .then(() => reload())
                .then(() =>
                  Toaster.success({
                    message: `${selection.count} Employee${pluralize(selection.count)} Unmark as ${
                      context === 'remote' ? 'Remote' : 'No Desk Needed'
                    }`,
                  })
                )
                .catch((error: ApolloError) =>
                  Toaster.fail({
                    error,
                  })
                )

              track.employees({
                eventName: Event.BATCH_ACTION,
                props: {
                  actionName: 'Remote status',
                  actionValue: 'Unmark as "Remote',
                  batchCount: selection.count,
                },
              })
            }}
          />
        </Menu>
      }>
      <Button
        disabled={disabled}
        loading={loading}
        data-button={context === 'remote' ? '"change_remote"' : 'change_no_desk_needed'}
        text={`Change "${context === 'remote' ? 'Remote' : 'No Desk Needed'}" status`}
        rightIconName="chevron-down"
      />
    </Popover>
  )
}
